/**
 * Reliable scroll utilities for smooth scrolling and section detection
 */

/**
 * Smooth scroll to element by ID within main-scroll-container
 */
export const smoothScrollToElement = (elementId, offset = 0) => {
  const element = document.getElementById(elementId);
  const container = document.getElementById('main-scroll-container');

  if (!element) {
    return false;
  }

  if (container) {
    // Get the element's position relative to the container
    const elementTop = element.offsetTop;
    const targetScroll = Math.max(0, elementTop - offset);

    // Use direct scrollTo with smooth behavior
    container.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });
  } else {
    // Fallback to window scroll
    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    const targetScroll = Math.max(0, elementTop - offset);

    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });
  }

  return true;
};

/**
 * Set up Intersection Observer to track active section
 * Call this once during component mount
 */
export const setupSectionObserver = (refs, setActiveSection) => {
  if (!refs || Object.keys(refs).length === 0) {
    return null;
  }

  const container = document.getElementById('main-scroll-container');

  // Observer configuration for container-based scrolling
  const observerOptions = {
    root: container,
    // Trigger closer to the top and avoid rapid switching on small sections
    rootMargin: '-15% 0px -55% 0px',
    threshold: [0.2, 0.35, 0.5, 0.65, 0.8],
  };

  const observer = new IntersectionObserver(
    (entries) => {
      let visibleSection = null;
      let maxVisibility = 0;

      // Find most visible section with a small hysteresis to reduce jumpiness
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const ratio = entry.intersectionRatio;
          if (ratio > maxVisibility + 0.02) {
            maxVisibility = ratio;
            visibleSection = entry.target.id;
          }
        }
      });

      // If no visible section, find closest to top with small offset
      if (!visibleSection) {
        let closestSection = null;
        let closestDistance = Infinity;

        entries.forEach((entry) => {
          const distance = Math.abs(entry.boundingClientRect.top - 60);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = entry.target.id;
          }
        });

        visibleSection = closestSection;
      }

      if (visibleSection) {
        setActiveSection(visibleSection);
      }
    },
    observerOptions
  );

  // Observe all refs
  Object.entries(refs).forEach(([id, ref]) => {
    if (ref?.current) {
      observer.observe(ref.current);
    }
  });

  return observer;
};
